import React, { useState, useEffect } from 'react';

// axios
import axios from 'axios';
import { apiClient } from 'api-client';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { setRefresh } from 'redux/actions/pageActions';
import { setContainers } from 'redux/actions/containersActions';
import { setWarehouses } from 'redux/actions/containersActions';

// language
import { injectIntl, IntlShape } from 'react-intl';

// extracted data
import { extractDataContainers, extractDataWarehouses } from 'common/extractData';

// components
import ErrorComponent from 'components/ErrorComponent';
import ContainersTable from 'pages/containers/containersTable/ContainersTable';
import WarehousesTable from 'pages/containers/containersTable/WarehousesTable';

// components-lib
import Legend from 'components-lib/Legend';

// MUI components
import { Grid } from '@mui/material';
import MDBox from "components/MDBox";
import LoadingComponent from 'components/LoadingComponent';

// types
import { ContainersTableType, WarehouseStatsType } from 'types/types';
import { Refresh } from 'types/typesRedux';

type Props = {
  intl: IntlShape,
}

const ContainersPage = ( {intl}: Props ) => {

  const dispatch = useDispatch();

  const refresh = useSelector( (state: Refresh) => state.handleRefresh);

  const [ localLoading, setLocalLoading ] = useState({
    containers: true,
    warehouses: true,
  });
  
  const [ isError, setIsError ] = useState({
    containers: false,
    warehouses: false,
  });

  const [warningTime, setWarningTime] = useState(null);

  const handleLoading = ( ifBool: boolean, setBool: boolean, name: keyof typeof localLoading ) => {
    if (localLoading[name] === ifBool) {setLocalLoading((prev) => ({
      ...prev,
      [name]: setBool,
      }));
    };
  }

  const handleError = ( ifBool: boolean, setBool: boolean, name: keyof typeof isError ) => {
    // console.log(setBool, name)
    if (isError[name] === ifBool) {
      setIsError((prev) => ({
        ...prev,
        [name]: setBool,
      }));
    };
  }

  const getContainers = async () => {
    const cancelToken = axios.CancelToken.source()
    
    try{
      const response = await apiClient.get("/v1/containers",{
        cancelToken:cancelToken.token
      });

      if (response) {
        const responseData = response.data.items.map( 
          ( containers: unknown ) => extractDataContainers(intl)(containers as ContainersTableType) );
          const totalCount = response.data.totalCount;
          // console.log(responseData)
        dispatch(setContainers( responseData, totalCount ));
        handleError(true, false, 'containers')
      } else {
        console.log("No response received.");
      }
    } catch (err) {
      console.log(err)
      if (axios.isCancel(err)) {
        console.log("canceled");
      } else {
        handleError(false, true, 'containers')
      }
    }
    handleLoading(true, false, 'containers')
    return () => cancelToken.cancel()
  };

  const getStats = async () => {
    const cancelToken = axios.CancelToken.source()
    
    try{
      const response = await apiClient.get("/v1/items-stats/by-location",{
        cancelToken:cancelToken.token
      });

      if (response) {
        const responseData = extractDataWarehouses(intl)(response.data);
        dispatch(setWarehouses( responseData as WarehouseStatsType[] ));
        handleError(true, false, 'warehouses')
      } else {
        console.log("No response received.");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("canceled");
      } else {
        handleError(false, true, 'warehouses')
      }
    }
    handleLoading(true, false, 'warehouses')
    return () => cancelToken.cancel()   
  };

  const getSettings = async () => {
    const cancelToken = axios.CancelToken.source()
    
    try{
      const response = await apiClient.get("/v1/settings",{
        cancelToken:cancelToken.token
      });

      if (response) {
        let responseTime = Number(response.data.doorOpenWarningAfter) / 60;
        // console.log(responseTime)
        setWarningTime(responseTime);
      } else {
        console.log("No response received.");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("canceled");
      } else {
        // todo:handle error
      }
    }
    return () => cancelToken.cancel()   
  };
  
  // Refresh when open page
  useEffect(() => {
    if ( refresh.refresh === true ) {
      dispatch(setRefresh( { refresh: false } ));
    }

    getContainers();

    const interval = setInterval(getContainers, 60000);
    
    return () => clearInterval(interval);
  },[] )

  useEffect(() => {
    getStats();
    getSettings();
  }, [])

  useEffect(() => {
    if ( refresh.refresh === true ) {
      dispatch(setRefresh( { refresh: false } ));
      getContainers();
      getStats();
      getSettings();
    }    
  },[refresh.refresh] )

  if ( isError.containers || isError.warehouses ) {
    return <ErrorComponent onPress={() => dispatch(setRefresh( { refresh: true } ))} />;
  }

  if ( localLoading.containers || localLoading.warehouses ) {
    return <LoadingComponent />;
  }

  return (
    <Grid
      sx={{ 
        maxWidth: "100rem", 
        width: 'auto',
      }}
    >      
      <ContainersTable
        warningTime={warningTime}
      />
      <WarehousesTable />
      <MDBox
        className= "table"
        sx={{ 
          marginLeft: "max(18.7rem)", 
          paddingRight: "1rem", 
          display: "flex", flexDirection: "column", justifyContent: "center",
          marginBottom: "1rem", 
        }}
      >
        <Legend
          warningTime={warningTime}
        />
      </MDBox>
    </Grid>  
  )
}

export default injectIntl(ContainersPage);